import React, { useContext, useMemo } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { CustomModal, UiButton } from '@shared/ui';
import { fetchEditPayment } from '@widgets/PaymentsList/lib/model/PaymentListApi';

import cls from './SecondStep.module.css';
import { Form } from '../../../../../components/UI/Form/Form';
import {
    fetchAddPayment,
    fetchPaymentsForEdit,
} from '../../../../../store/slices/paymentSlice/action';
import { ModalContext } from '../AddPaymentModal';
import { DebitModal } from '../DebitModal/DebitModal';

export const SecondStep = ({
    modalState,
    setModalState,
    onHideModal,
    accountsSelectList,
    payment = null,
    isCanDeleteUnsettled,
    deleteUndistributedPayment,
}) => {
    const { t } = useTranslation();
    const pagination = useSelector((state) => state.paymentReducer.pagination);
    const dispatch = useDispatch();

    const paymentGuid = payment ? payment.guid : '';
    const {
        formState,
        setErrors,
        selectedAccount,
        selectedFirstStepAccount,
        setNeedUpdateExtraData,
    } = useContext(ModalContext);

    const filteredAccountsSelectList = useMemo(
        () =>
            accountsSelectList.filter(
                (acc) => acc?.account?.guid !== selectedAccount?.account?.guid
            ) || accountsSelectList,
        [accountsSelectList, selectedAccount?.account?.guid]
    );

    const onSubmitButton = async () => {
        const requiredReceiveAccount =
            modalState.isBetweenAccount === false ? true : !!formState.firstStepAccount;
        if (formState.purposePayment && requiredReceiveAccount) {
            if (modalState.addPaymentMode === 'distribution') {
                // loader.show();

                const callback = () => {
                    onHideModal();

                    dispatch(fetchPaymentsForEdit(pagination.current));

                    // loader.hide();
                };

                const formData = new FormData();

                if (modalState.isBetweenAccount) {
                    if (payment.type === 'credit') {
                        formData.append('account_guid', formState.account);
                        formData.append('receiver_account_guid', formState.firstStepAccount);
                    } else {
                        formData.append('account_guid', formState.firstStepAccount);
                        formData.append('receiver_account_guid', formState.account);
                    }

                    formData.append('payment_guid', paymentGuid);
                } else {
                    formData.append('paymentDate', formState.paymentDate);
                    formData.append('accrualDate', formState.accrualDate);
                    formData.append('account', formState.account);
                    formData.append('target', formState.purposePayment);
                    formData.append('sum', formState.sum.replace(/,/g, '.'));

                    formData.append('correspondent', formState.partners ? formState.partners : 0);
                    formData.append(
                        'paymentItem',
                        formState.expenditures ? formState.expenditures : 0
                    );

                    if (formState.distributeEstimates.length === 0) {
                        formData.append(
                            'divisions[0][estimateOrProject]',
                            formState.estimates ? formState.estimates : ''
                        );
                        formData.append('divisions[0][percent]', 100);
                        formData.append('divisions[0][sum]', formState.sum.replace(/,/g, '.'));
                    } else {
                        formState.distributeEstimates.forEach((estimate, index) => {
                            formData.append(
                                `divisions[${index}][estimateOrProject]`,
                                estimate.estimateSelect
                            );
                            formData.append(`divisions[${index}][percent]`, estimate.percent);
                            formData.append(`divisions[${index}][sum]`, estimate.sum);
                        });
                    }
                }

                formData.append('comment', formState.comment);
                formState.invoicePayment.forEach((file) => {
                    formData.append('attachments[]', file);
                });
                const errorHandler = (data) => {
                    loader.hide();
                    const errorInit = {
                        account: null,
                        sum: null,
                        paymentItem: null,
                        estimate: null,
                        correspondent: null,
                        receiverAccount: null,
                        purposePayment: null,
                        fullName: null,
                        bik: null,
                        accountNumber: null,
                    };

                    data.errors.forEach((error) => {
                        errorInit[error.name] = error.messages.toString();
                    });
                    setErrors(errorInit);
                };

                fetchEditPayment(
                    payment.guid,
                    formData,
                    callback,
                    errorHandler,
                    modalState.isBetweenAccount
                );
            } else {
                loader.show();
                const addDebitUrl = routable.getRoute('addDebitPaymentForReact');
                const addCreditUrl = routable.getRoute('addCreditPaymentForReact');
                const addTransferUrl = routable.getRoute('addTransferPaymentForReact');
                const formData = new FormData();
                if (
                    (modalState.isBetweenAccount && modalState.mode === 'credit') ||
                    modalState.mode === 'transfer'
                ) {
                    formData.append('account', formState.account);
                    formData.append('receiverAccount', formState.firstStepAccount);
                    formData.append('sum', formState.sum.replace(/,/g, '.'));
                    if (selectedAccount.account.integration) {
                        formData.append('receiveSum', formState.sum.replace(/,/g, '.'));
                    } else if (
                        selectedFirstStepAccount.account.currency.sign ===
                        selectedAccount.account.currency.sign
                    ) {
                        formData.append('receiveSum', formState.sum.replace(/,/g, '.'));
                    } else {
                        formData.append('receiveSum', formState.receiveSum.replace(/,/g, '.'));
                    }
                } else if (modalState.isBetweenAccount && modalState.mode === 'debit') {
                    formData.append('account', formState.firstStepAccount);
                    formData.append('receiverAccount', formState.account);
                    if (selectedAccount.account.integration) {
                        formData.append('sum', formState.sum.replace(/,/g, '.'));
                        formData.append('receiveSum', formState.sum.replace(/,/g, '.'));
                    } else if (
                        selectedFirstStepAccount.account.currency.sign ===
                        selectedAccount.account.currency.sign
                    ) {
                        formData.append('receiveSum', formState.sum.replace(/,/g, '.'));
                        formData.append('sum', formState.sum.replace(/,/g, '.'));
                    } else {
                        formData.append('receiveSum', formState.sum.replace(/,/g, '.'));
                        formData.append('sum', formState.receiveSum.replace(/,/g, '.'));
                    }
                } else {
                    formData.append('account', formState.account);
                    formData.append('sum', formState.sum.replace(/,/g, '.'));
                }

                if (selectedAccount.account.integration) {
                    formData.append('name', formState.fullName);
                    formData.append('account_number', formState.accountNumber);
                    formData.append('inn', formState.inn);
                    formData.append('kpp', formState.kpp);
                    formData.append('bik', formState.bik);
                }

                formData.append('paymentDate', formState.paymentDate);
                if (modalState.addPaymentMode === 'applications') {
                    formData.append('paymentRequestGuid', payment.guid);
                }
                formData.append('accrualDate', formState.accrualDate);
                formData.append('target', formState.purposePayment);
                formData.append('correspondent', formState.partners ? formState.partners : 0);
                formData.append(
                    'paymentItem',
                    formState.expenditures ? formState.expenditures : ''
                );
                if (formState.distributeEstimates.length === 0) {
                    formData.append(
                        'divisions[0][estimateOrProject]',
                        formState.estimates ? formState.estimates : ''
                    );
                    formData.append('divisions[0][percent]', 100);
                    formData.append('divisions[0][sum]', formState.sum.replace(/,/g, '.'));
                } else {
                    formState.distributeEstimates.forEach((estimate, index) => {
                        formData.append(
                            `divisions[${index}][estimateOrProject]`,
                            estimate.estimateSelect ? estimate.estimateSelect : ''
                        );
                        formData.append(`divisions[${index}][percent]`, estimate.percent);
                        formData.append(`divisions[${index}][sum]`, estimate.sum);
                    });
                }

                formData.append('comment', formState.comment);

                formState.invoicePayment.forEach((file) => {
                    formData.append('attachments[]', file);
                });

                const callback = async (data) => {
                    await setModalState((prevState) => ({
                        ...prevState,
                        isOpen: false,
                        step: 1,
                        mode: 'debit',
                    }));
                    await setErrors(null);

                    if (modalState.addPaymentMode === 'applications') {
                        await $.modalable('closeAll');

                        await companyPaymentManager.tabs.getActiveTabConfig().requestData();
                    }
                };

                const bankIntegrationErrorHandler = (data) => {
                    swal({
                        text: data.message,
                        cancel: false,
                        confirm: true,
                        button: {
                            text: t('SecondStep.bankIntegrationErrorHandler.text'),
                            value: true,
                            visible: true,
                            className: 'btn btn-primary',
                        },
                    });
                    loader.hide();
                };

                const errorHandler = (data) => {
                    loader.hide();
                    const errorInit = {
                        account: null,
                        sum: null,
                        paymentItem: null,
                        estimate: null,
                        correspondent: null,
                        receiverAccount: null,
                        purposePayment: null,
                        fullName: null,
                        bik: null,
                        accountNumber: null,
                        receiveSum: null,
                    };

                    data.errors.forEach((error) => {
                        errorInit[error.name] = error.messages.toString();
                    });
                    setErrors(errorInit);
                };
                if (modalState.isBetweenAccount) {
                    await dispatch(
                        fetchAddPayment({
                            url: addTransferUrl,
                            formData,
                            page: 1,
                            callback,
                            errorHandler,
                            bankIntegrationErrorHandler,
                        })
                    );
                } else {
                    if (modalState.mode === 'debit') {
                        await dispatch(
                            fetchAddPayment({
                                url: addDebitUrl,
                                formData,
                                page: 1,
                                callback,
                                errorHandler,
                                bankIntegrationErrorHandler,
                            })
                        );
                    }
                    if (modalState.mode === 'credit') {
                        await dispatch(
                            fetchAddPayment({
                                url: addCreditUrl,
                                formData,
                                page: 1,
                                callback,
                                errorHandler,
                                bankIntegrationErrorHandler,
                            })
                        );
                    }
                }
                await setModalState((prevState) => ({
                    ...prevState,
                    isOpen: false,
                    step: 1,
                    mode: 'debit',
                }));
                setErrors(null);
            }
        } else {
            setErrors((prevState) => ({
                ...prevState,
                purposePayment: t('SecondStep.onSubmitButton.setErrors'),
            }));
        }
    };

    const onBackButton = () => {
        const prevStep = selectedAccount.account.integration ? 3 : 1;
        setNeedUpdateExtraData(false);
        setModalState((prev) => ({ ...prev, step: prevStep }));
    };

    return (
        <>
            <CustomModal.Body>
                <Form>
                    <div className={classNames(cls.formBlock)}>
                        <div className={classNames(cls.stepList)}>
                            <span className={classNames(cls.stepListItem)}>
                                {modalState.mode === 'debit'
                                    ? t('SecondStep.return.debit')
                                    : t('SecondStep.return.credit')}
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M6.00005 10.8619L13.5286 3.33331L14.4715 4.27612L6.00005 12.7475L2.19531 8.94279L3.13812 7.99998L6.00005 10.8619Z"
                                        fill="#27AE60"
                                    />
                                </svg>
                            </span>
                            {selectedAccount.account.integration && (
                                <span className={classNames(cls.stepListItem)}>
                                    {modalState.mode === 'debit'
                                        ? t('SecondStep.stepListItem.debit')
                                        : t('SecondStep.stepListItem.credit')}
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M6.00005 10.8619L13.5286 3.33331L14.4715 4.27612L6.00005 12.7475L2.19531 8.94279L3.13812 7.99998L6.00005 10.8619Z"
                                            fill="#27AE60"
                                        />
                                    </svg>
                                </span>
                            )}
                            <span className={classNames(cls.stepListItem)}>
                                {t('SecondStep.step')}{' '}
                                {selectedAccount.account.integration ? '3' : '2'}.{' '}
                                {t('SecondStep.details')}
                            </span>
                        </div>
                        <DebitModal
                            modalState={modalState}
                            accountsSelectList={filteredAccountsSelectList}
                            setModalState={setModalState}
                        />
                    </div>
                </Form>
            </CustomModal.Body>
            <CustomModal.Footer>
                <div className={classNames(cls.footer)}>
                    <UiButton
                        onClick={onHideModal}
                        btnColor="secondary"
                        btnText={t('SecondStep.footer.cancel')}
                    />
                    {isCanDeleteUnsettled && (
                        <UiButton
                            btnColor="danger"
                            className={cls.deleteButton}
                            onClick={() => deleteUndistributedPayment?.()}
                            btnText={t('button.delete.text')}
                        />
                    )}
                    <div className={classNames(cls.buttonGroup)}>
                        <UiButton
                            onClick={onBackButton}
                            btnColor="secondary"
                            btnText={t('SecondStep.footer.back')}
                        />
                        <UiButton onClick={onSubmitButton} btnText={t('button.save.text')} />
                    </div>
                </div>
            </CustomModal.Footer>
        </>
    );
};
